import React, {useState} from 'react';
import moment from 'moment-timezone';
import tinycolor from 'tinycolor2';

import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomDialog from '../common/CustomDialog';
import AwayTimeSummary from './AwayTimeSummary';
import EventDetails from './EventDetails';
import ProviderAvailabilitySummary from './ProviderAvailabilitySummary';

import config from '../../config/config';
import { getLocationColor, getLocationEmoji } from '../../helpers/locations';
import { formatPatientName } from '../../helpers/patients';

const SummaryPopover = ({
  block,
  open,
  anchorEl,
  onClose,
}) => {
  const { 
    isAwayTime, 
    isAvailabilityBlock, 
    awayTime, 
    availability 
  } = block;
  return (
    <Popover 
      id={'away-time-' + awayTime?.id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // disableRestoreFocus
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      // sx={{ pointerEvents: 'none' }}
    >
      {/* {isAwayTime && <AwayTimeSummary awayTime={awayTime}/>} */}
      {isAvailabilityBlock && 
        <ProviderAvailabilitySummary 
          availability={availability} 
          onClose={onClose}
        />
      }
    </Popover>
  );
};

const BlockDialog = ({
  isAwayTime,
  isEvent,
  event,
  isOpen,
  onClose,
  maxWidth = 'xs',
}) => {
  if (!isAwayTime && !isEvent) return null;
  const title = isAwayTime || isEvent ? 'Time Block Details' : '';
  return (
    <CustomDialog
      open={isOpen}
      title={title}
      onClose={() => onClose()}
      maxWidth={maxWidth}
    >
      <EventDetails event={event} afterDelete={() => onClose()} />
    </CustomDialog>
  );
}

const getTitle = ({block, isAwayTime, isEvent, isAvailabilityBlock}) => {
  if (isAwayTime) return 'Away Time';
  if (isAvailabilityBlock && block?.title) return block.title;
  if (isEvent) return block.event?.title || 'Event';
  if (!!block?.patient) return formatPatientName(block.patient);
  return null;
};

const getEmoji = ({isAwayTime, isAvailabilityBlock, isEvent, locationCategory}) => {
  if (isAwayTime) return '❌';
  if (isAvailabilityBlock) return '⚙️';
  if (isEvent) return '📅';
  return getLocationEmoji(locationCategory);
}

const handleClick = ({
  isAwayTime,
  isEvent,
  isAvailabilityBlock,
  useSummaries,
  onClick,
  block,
  openDialog,
  openSummary,
}) => e => {
  if (isAwayTime || isEvent) return openDialog();
  if (isAvailabilityBlock && useSummaries) openSummary(e);
  if (onClick) onClick(e, block.id);
  return undefined;
};

/**
 * Renders a single appointment block overlayed on the week grid.
 * Expects appointment: { start, end, backgroundColor, title? }
 * and style: { top, left, width, height } in pixels.
 */
const CalendarBlock = ({
  timezone,
  block,
  style,
  isAvailabilityBlock = false,
  useSummaries = false,
  useSubtleAvailability = false,
  onClick = (e, id) => console.log('Clicked time block', e, id),
}) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const event = block?.event;
  const isAwayTime = block?.event && block.event.event_category === 'AWAY_TIME';
  const isEvent = block?.event && block.event.event_category !== 'AWAY_TIME';
  // if (block?.event) console.log('isAwayTime', isAwayTime, 'isEvent', isEvent, 'isAvailabilityBlock', isAvailabilityBlock, block, event);
  const [anchorEl, setAnchorEl] = useState(null);
  if (!block || !style) return null;
  const startDate = (
    typeof block.date === 'string'
    ? new Date(block.date)
    : block.date
  );
  const timeLabel = moment(startDate).tz(timezone || moment.tz.guess()).format('h:mm A');
  const locationCategory = (
    block?.appointment_scheduled?.location_type // new version
    || block?.appointment_scheduled?.session_type?.location_type
    || block?.session_type?.location_type
  );
  const alpha = (
    isAvailabilityBlock 
    ? (useSubtleAvailability ? 0.1 : 0.4)
    : 1.0
  ); 
  const backgroundColor = (
    isAvailabilityBlock 
    ? (tinycolor(block.css_color || '#4c98af').setAlpha(alpha).toRgbString())
    : isAwayTime ? 'rgba(200, 200, 200, 0.8)'
    : isEvent ? config().colors.info
    : getLocationColor(locationCategory)
  );
  const opacity = moment(block.date).isBefore(moment()) ? 0.60 : 1;
  const border = (
    isAvailabilityBlock
    ? (useSubtleAvailability ? 'none' : `1px solid ${block.css_color || '#4c98af'}`)
    : undefined
  );
  const borderLeft = (
    isAvailabilityBlock
    ? (useSubtleAvailability ? `4px solid ${tinycolor(block.css_color || '#4c98af').setAlpha(0.3).toRgbString()}` : `4px solid ${block.css_color || '#4c98af'}`)
    : undefined
  );
  const fontColor = (
    isAvailabilityBlock 
    ? (block.css_color || '#4c98af') 
    : 'white'
  );
  let zIndex = 4;
  if (isAwayTime || isEvent) zIndex = 3;
  if (isAvailabilityBlock) zIndex = 2;
  const _onClick = handleClick({
    isAwayTime, 
    isEvent, 
    isAvailabilityBlock, 
    useSummaries, 
    onClick,
    block, 
    openDialog: () => setDialogIsOpen(true), 
    openSummary: e => setAnchorEl(anchorEl ? null : e.currentTarget)
  }  );
  const title = getTitle({block, isAwayTime, isEvent, isAvailabilityBlock});
  const emoji = getEmoji({isAwayTime, isAvailabilityBlock, isEvent, locationCategory});
  return (
    <>
      <Box
        style={{
          cursor: _onClick ? 'pointer' : 'default',
          position: 'absolute',
          top: style.top,
          left: style.left,
          width: style.width,
          height: style.height,
          backgroundColor,
          color: fontColor,
          fontWeight: 700,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          zIndex,
          opacity,
          border,
          borderLeft,
        }}
        sx={{
          borderRadius: isAvailabilityBlock ? 0 : 2,
          pt: 0.5,
          pr: 1,
          pl: 1,
          pb: 0.5,
          boxShadow: 1,
          '&:hover': {
            opacity: 0.9,
            boxShadow: 2,
          },
        }}
        onClick={_onClick}
        // aria-owns={!!anchorEl ? 'mouse-over-popover' : undefined}
        // aria-haspopup='true'
        // onMouseEnter={e => setAnchorEl(e.currentTarget)}
        // onMouseLeave={e => setAnchorEl(null)}
      >
        <Stack direction='row' justifyContent='space-between'>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: fontColor, lineHeight: 0.9 }}
          >
            {title}
          </Typography>
          <Typography>{emoji}</Typography>
        </Stack>
        {!!title &&
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: fontColor,
              overflow: 'hidden',
              textOverflow: isAvailabilityBlock ? null : 'ellipsis',
              whiteSpace: isAvailabilityBlock ? 'normal' : 'nowrap',
              lineHeight: 1.1,
              m: 0,
              p: 0,
            }}
          >
            {timeLabel}
          </Typography>
        }
        {!!block.duration && block.duration > 30 &&
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 0.9,
              pt: 0.5,
            }}
          >
            {(block.location !== 'VIRTUAL' 
              ? block.appointment_scheduled?.location?.name || block.location 
              : ''
            ) || ''}
          </Typography>
        }
      </Box>
      {isAvailabilityBlock && useSummaries && <SummaryPopover
        block={block}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      />}
      {isEvent || isAwayTime ? 
        <BlockDialog
          maxWidth='xs'
          isAwayTime={isAwayTime}
          isEvent={isEvent}
          event={event}
          isOpen={dialogIsOpen}
          onClose={() => setDialogIsOpen(false)}
        /> 
        : null
      }
    </>
  );
};

export default CalendarBlock;
